import React from 'react';
import { Link } from 'react-router-dom';
import { SolutionItem } from '../../utils/ourSolutionData';
import { partnersData } from '../../utils/partnersData';
import { useTranslation } from 'react-i18next';

type SolutionDetailCardProps = {
  item: SolutionItem;
  textFirst: boolean;
  subCategoryLinks?: { label: string; href: string }[];
};

const SolutionDetailCard: React.FC<SolutionDetailCardProps> = ({ item, textFirst, subCategoryLinks = [] }) => {
  const { t } = useTranslation();
  const hasDynamicSubCategories = subCategoryLinks.length > 0;
  const subItems = hasDynamicSubCategories ? subCategoryLinks : [];
  const toI18nKey = (value: string) =>
    value
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  const normalizePartnerKey = (value: string) => value.trim().toLowerCase();
  const partnerAnchorByName: Record<string, string> = partnersData.reduce((acc, partner) => {
    acc[normalizePartnerKey(partner.name)] = partner.anchorId;
    return acc;
  }, {} as Record<string, string>);
  partnerAnchorByName.dahua = 'partner-5';
  partnerAnchorByName.hpe = 'partner-3';
  const getPartnerLink = (partnerName: string): string | null => {
    const key = normalizePartnerKey(partnerName);
    const anchorId = partnerAnchorByName[key];
    return anchorId ? `/partners#${anchorId}` : "/partners";
  };

  const localizedTitle = t(`ourSolutionSection.items.${item.slug}.title`, { defaultValue: item.title });
  const localizedDetailTitle = t(`solutionsPageSection.items.${item.slug}.detailTitle`, { defaultValue: item.detailTitle });
  const localizedDetailDescription = t(`solutionsPageSection.items.${item.slug}.detailDescription`, {
    defaultValue: item.detailDescription
  });
  const localizedApplications = item.applications.map((app) =>
    t(`solutionsPageSection.items.${item.slug}.applications.${app}`, { defaultValue: app })
  );

  return (
    <article id={item.slug} className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center scroll-mt-28">
      <div className={`order-2 ${textFirst ? 'lg:order-1' : 'lg:order-2'}`}>
        <p className="text-teal-600 text-xs font-bold uppercase tracking-[0.2em]">{localizedTitle}</p>
        <h2 className="mt-3 tracking-tight">
          <Link
            to={`/solutions/${item.slug}`}
            className="text-2xl sm:text-3xl font-bold text-[#0F172A] hover:text-teal-700 transition-colors"
          >
            {localizedDetailTitle}
          </Link>
        </h2>
        <p className="text-[#64748B] mt-4 leading-relaxed">{localizedDetailDescription}</p>

        <div className="mt-6">
          <h3 className="text-xs font-bold text-[#0F172A] uppercase tracking-[0.15em] mb-3">{t('solutionsPageSection.subCategoriesLabel')}</h3>
          {hasDynamicSubCategories ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {subItems.map((sub) => (
                <Link
                  key={`${item.slug}-${sub.label}`}
                  to={sub.href}
                  className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-3.5 py-2 text-xs font-semibold text-blue-700 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-blue-400 hover:bg-blue-100 hover:text-blue-800 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
                >
                  {t(`solutionsPageSection.items.${item.slug}.subCategories.${toI18nKey(sub.label)}`, { defaultValue: sub.label })}
                </Link>
              ))}
            </div>
          ) : (
            <div className="mt-3 flex flex-wrap gap-2 animate-pulse">
              <span className="h-7 w-28 rounded-lg bg-[#F7F8FA]" />
              <span className="h-7 w-36 rounded-lg bg-[#F7F8FA]" />
              <span className="h-7 w-24 rounded-lg bg-[#F7F8FA]" />
            </div>
          )}
        </div>

        <div className="mt-5">
          <h3 className="text-xs font-bold text-[#0F172A] uppercase tracking-[0.15em] mb-3">{t('solutionsPageSection.technologyPartnersLabel')}</h3>
          <div className="flex flex-wrap gap-2">
            {item.technologyPartners.map((partner) => {
              const partnerLink = getPartnerLink(partner);
              return partnerLink ? (
                <Link
                  key={partner}
                  to={partnerLink}
                  className="inline-flex items-center rounded-full border border-teal-200 bg-teal-50 px-3.5 py-2 text-xs font-semibold text-teal-700 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-teal-300 hover:bg-teal-100 hover:text-teal-800 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/40"
                >
                  {partner}
                </Link>
              ) : (
                <span
                  key={partner}
                  className="inline-flex items-center rounded-full border border-teal-200 bg-teal-50 px-3.5 py-2 text-xs font-semibold text-teal-700 shadow-sm"
                >
                  {partner}
                </span>
              );
            })}
          </div>
        </div>

        <div className="mt-5">
          <h3 className="text-xs font-bold text-[#0F172A] uppercase tracking-[0.15em] mb-3">{t('solutionsPageSection.applicationsLabel')}</h3>
          <div className="flex flex-wrap gap-2">
            {localizedApplications.map((app) => (
              <span
                key={app}
                className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-gradient-to-r from-sky-50 to-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition-colors hover:border-sky-300"
              >
                <span className="h-1.5 w-1.5 rounded-none bg-sky-500" />
                {app}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className={`order-1 ${textFirst ? 'lg:order-2' : 'lg:order-1'} rounded-2xl overflow-hidden`}>
        {item.video ? (
          <video
            src={item.video}
            poster={item.image}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-label={localizedTitle}
            className="w-full h-72 lg:h-96 object-cover rounded-2xl"
          />
        ) : (
          <img src={item.image} alt={localizedTitle} className="w-full h-72 lg:h-96 object-cover rounded-2xl" />
        )}
      </div>
    </article>
  );
};

export default SolutionDetailCard;
